import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { useEffect } from "react";
import Layout from "./components/layout/Layout";
import Index from "./pages/Index";
import Dashboard from "./pages/Dashboard";
import MFO from "./pages/MFO";
import MFOCodes from "./pages/MFOCodes";
import MFODetail from "./pages/MFODetail";
import KanbanBoard from "./pages/KanbanBoard";
import Divisions from "./pages/Departments";
import DivisionDetail from "./pages/DepartmentDetail";
import Calendar from "./pages/Calendar";
import Employees from "./pages/Employees";
import EmployeeDetailPage from "./pages/EmployeeDetail";
import NotFound from "./pages/NotFound";
import Login from "./pages/Login";
// import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import VerifyOTP from "./pages/VerifyOTP";
import ResetPassword from "./pages/ResetPassword";
import Notifications from "./pages/Notifications";
import Profile from "./pages/Profile";
import Settings from "./pages/Settings";
import { checkForApproachingDeadlines } from "./utils/supabase/realtimeUtils";
import { createNotification } from "./utils/notificationUtils";
import { toast } from "./hooks/use-toast";
import MFOSubTasks from "./pages/MFOSubTasks";
import PublicMFODetail from "@/pages/PublicMFODetail";
import EditMFO from "@/pages/EditMFO";
import MFOCodeDetail from "./pages/MFOCodeDetail";
import MFOPresentation from "./pages/MFOPresentation";
// Create a client with significantly improved caching defaults
const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 3 * 60 * 1000, // 3 minutes before data is considered stale
			gcTime: 5 * 60 * 1000, // 5 minutes before unused data is garbage collected
			refetchOnWindowFocus: true,
			refetchOnMount: true,
			refetchOnReconnect: true,
			retry: 3, // Increased retry attempts
			retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
		},
	},
});

// Create a component that listens for page visibility changes and checks deadlines
const QueryRefresher = () => {
	useEffect(() => {
		// Function to handle visibility change
		const handleVisibilityChange = () => {
			if (document.visibilityState === "visible") {
				// When the page becomes visible again, check all queries
				queryClient.invalidateQueries();

				// Check for approaching deadlines and create notifications
				checkForApproachingDeadlines()
					.then((deadlines) => {
						deadlines.forEach((mfo) => {
							const dueDate = new Date(mfo.due_date);
							const today = new Date();
							const daysUntilDue = Math.ceil(
								(dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
							);

							// Create notification for approaching deadline
							createNotification(
								"Deadline Approaching",
								`MFO "${mfo.code}: ${mfo.title}" is due in ${daysUntilDue} day${daysUntilDue !== 1 ? "s" : ""}`,
								"deadline_approaching"
							);
						});
					})
					.catch((error) => {
						console.error("Error checking deadlines:", error);
					});
			}
		};

		// Listen for visibility changes (tab switching or navigating back to the app)
		document.addEventListener("visibilitychange", handleVisibilityChange);

		// Also check on page load
		if (document.visibilityState === "visible") {
			queryClient.invalidateQueries();

			// Initial deadline check
			checkForApproachingDeadlines()
				.then((deadlines) => {
					deadlines.forEach((mfo) => {
						const dueDate = new Date(mfo.due_date);
						const today = new Date();
						const daysUntilDue = Math.ceil(
							(dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
						);

						createNotification(
							"Deadline Approaching",
							`MFO "${mfo.code}: ${mfo.title}" is due in ${daysUntilDue} day${daysUntilDue !== 1 ? "s" : ""}`,
							"deadline_approaching"
						);
					});
				})
				.catch((error) => {
					console.error("Error checking deadlines:", error);
				});
		}

		// Setup page reload handling - store an indicator in localStorage (not sessionStorage)
		// to persist across browser restarts
		const handleBeforeUnload = () => {
			localStorage.setItem("last_app_reload", Date.now().toString());
		};

		window.addEventListener("beforeunload", handleBeforeUnload);

		const lastReload = localStorage.getItem("last_app_reload");
		const currentTime = Date.now();
		if (lastReload) {
			const timeSinceLastReload = currentTime - parseInt(lastReload);
			if (timeSinceLastReload < 10000) {
				queryClient.invalidateQueries();
			}
		}
		const refreshInterval = setInterval(
			() => {
				if (document.visibilityState === "visible") {
					queryClient.invalidateQueries();
				}
			},
			2 * 60 * 1000
		); // Refresh every 2 minutes if tab is visible

		return () => {
			document.removeEventListener("visibilitychange", handleVisibilityChange);
			window.removeEventListener("beforeunload", handleBeforeUnload);
			clearInterval(refreshInterval);
		};
	}, []);

	return null;
};

// Remove the React Query Devtools code that was causing issues

// Add a global error handler
window.addEventListener("unhandledrejection", (event) => {
	console.error("Unhandled promise rejection:", event.reason);
	toast({
		title: "An error occurred",
		description: "Please try refreshing the page if you experience problems",
		variant: "destructive",
	});
});

const App = () => (
	<QueryClientProvider client={queryClient}>
		<BrowserRouter>
			<QueryRefresher />
			<AuthProvider>
				<TooltipProvider>
					<Toaster />
					<Sonner />
					<Routes>
						<Route
							path='/'
							element={<Index />}
						/>
						<Route
							path='/login'
							element={<Login />}
						/>
						{/* <Route path="/signup" element={<Signup />} /> */}
						<Route
							path='/forgot-password'
							element={<ForgotPassword />}
						/>
						<Route
							path='/verify-otp'
							element={<VerifyOTP />}
						/>
						<Route
							path='/reset-password'
							element={<ResetPassword />}
						/>
						<Route
							path='/mfo/:id/public'
							element={<PublicMFODetail />}
						/>
						<Route
							path='/dashboard'
							element={<Layout />}>
							<Route
								index
								element={<Dashboard />}
							/>
							<Route
								path='divisions'
								element={<Divisions />}
							/>
							<Route
								path='divisions/:id'
								element={<DivisionDetail />}
							/>
							<Route
								path='mfo'
								element={<MFO />}
							/>
							<Route
								path='mfo-codes'
								element={<MFOCodes />}
							/>
							<Route
								path='mfo-codes/:id'
								element={<MFOCodeDetail />}
							/>
							<Route
								path='mfo/:id'
								element={<MFODetail />}
							/>
							<Route
								path='kanban'
								element={<KanbanBoard />}
							/>
							<Route
								path='calendar'
								element={<Calendar />}
							/>
							<Route
								path='sub-tasks'
								element={<MFOSubTasks />}
							/>
							<Route
								path='employees'
								element={<Employees />}
							/>
							<Route
								path='employees/:id'
								element={<EmployeeDetailPage />}
							/>
							<Route
								path='notifications'
								element={<Notifications />}
							/>
							<Route
								path='mfo/:id/edit'
								element={<EditMFO />}
							/>
							<Route
								path='profile'
								element={<Profile />}
							/>
							<Route
								path='settings'
								element={<Settings />}
							/>
							<Route
								path='mfo-presentation'
								element={<MFOPresentation />}
							/>
						</Route>
						<Route
							path='*'
							element={<NotFound />}
						/>
					</Routes>
				</TooltipProvider>
			</AuthProvider>
		</BrowserRouter>
	</QueryClientProvider>
);

export default App;
